import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const timer = useRef<number | null>(null);

  const count = movies.length;

  const goTo = (i: number) => setIndex(((i % count) + count) % count);

  useEffect(() => {
    if (count <= 1) return;
    timer.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, 6000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [count]);

  if (count === 0) return null;

  return (
    <section
      className="relative w-full h-[420px] md:h-[600px] overflow-hidden bg-foreground-950"
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStartX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(dx) > 50) goTo(dx < 0 ? index + 1 : index - 1);
        touchStartX.current = null;
      }}
    >
      {movies.map((m, i) => (
        <div
          key={m.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          <img
            src={m.backdrop}
            alt={`${m.title}剧照`}
            title={`${m.title} - 剧情与影评专题`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="absolute inset-0 z-20 flex items-center">
        <div className="w-full max-w-content mx-auto px-4 md:px-6">
          <div className="max-w-xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-semibold">
              <i className="ri-fire-fill"></i>
              焦点推荐
            </span>
            <h1 className="mt-4 text-3xl md:text-5xl font-bold text-white leading-tight">
              {movies[index].title}
            </h1>
            <p className="mt-2 text-sm md:text-base text-white/70">
              {movies[index].enTitle} · {movies[index].year} · {movies[index].genres.join(' / ')}
            </p>
            <p className="mt-4 text-sm md:text-base text-white/85 leading-relaxed line-clamp-2 md:line-clamp-3">
              {movies[index].tagline}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/movies/detail/${movies[index].id}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line w-5 h-5 flex items-center justify-center"></i>
                查看剧情与影评
              </Link>
              <Link
                to="/movies"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-white/40 text-white text-sm font-medium hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                浏览全部电影
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => goTo(index - 1)}
        className="hidden md:flex absolute left-4 lg:left-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => goTo(index + 1)}
        className="hidden md:flex absolute right-4 lg:right-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-5 md:bottom-7 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
        {movies.map((m, i) => (
          <button
            key={m.id}
            type="button"
            aria-label={`切换到 ${m.title}`}
            onClick={() => goTo(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-7 bg-accent-500' : 'w-3 bg-white/50 hover:bg-white/80'
            }`}
          />
        ))}
      </div>
    </section>
  );
}